// src/components/modals/ViewPurchaseOrderModal.jsx
// Updated version without question marks in status
import { X, ShoppingCart, Calendar, Package, MapPin, FileText, User, Building, CheckCircle, Clock, XCircle, AlertCircle, FileDown } from 'lucide-react'
import { PurchaseOrderUtils } from '../../utils/purchaseOrderUtils'

const ViewPurchaseOrderModal = ({ isOpen, onClose, purchaseOrder }) => {
  if (!isOpen || !purchaseOrder) return null

  // Enhanced status configuration to ensure no question marks
  const getCleanStatusConfig = (status) => {
    // Normalize status to lowercase and trim whitespace
    const normalizedStatus = status?.toString().toLowerCase().trim()
    
    const statusMap = {
      'pending': {
        label: 'Pending',
        icon: <Clock className="h-3 w-3" />,
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      },
      'approved': {
        label: 'Approved',
        icon: <CheckCircle className="h-3 w-3" />,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200'
      },
      'completed': {
        label: 'Completed',
        icon: <CheckCircle className="h-3 w-3" />,
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      'complete': {
        label: 'Completed',
        icon: <CheckCircle className="h-3 w-3" />,
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      'cancelled': {
        label: 'Cancelled',
        icon: <XCircle className="h-3 w-3" />,
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      },
      'rejected': {
        label: 'Rejected',
        icon: <XCircle className="h-3 w-3" />,
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      }
    }

    // Return the matching status or default with CheckCircle for any completed-like status
    const matchedStatus = statusMap[normalizedStatus]
    if (matchedStatus) {
      return matchedStatus
    }

    // If status contains "complete" anywhere, use the completed style with tick
    if (normalizedStatus?.includes('complete')) {
      return {
        label: 'Completed',
        icon: <CheckCircle className="h-3 w-3" />,
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      }
    }

    // Default fallback
    return {
      label: status || 'Unknown',
      icon: <AlertCircle className="h-3 w-3" />,
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      borderColor: 'border-gray-200'
    }
  }

  const statusConfig = getCleanStatusConfig(purchaseOrder.status)

  const generatePOPDF = () => {
    const po = purchaseOrder
    const supplier = po.supplier || po.warehouse || {}
    const supplierLabel = po.supplierType === 'supplier' ? 'Supplier' : 'Warehouse'
    const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'

    const itemRows = (po.orderItems || []).map((item, i) => {
      const snap = item.productSnapshot || item.rawMaterialSnapshot || {}
      const type = item.itemType === 'product' ? 'Product' : 'Raw Material'
      return `
        <tr style="border-bottom:1px solid #e5e7eb;">
          <td style="padding:10px 12px;">${i + 1}</td>
          <td style="padding:10px 12px;font-weight:600;">${snap.name || 'N/A'}</td>
          <td style="padding:10px 12px;color:#6b7280;font-size:12px;">${snap.sku || snap.code || '-'}</td>
          <td style="padding:10px 12px;"><span style="background:${item.itemType === 'product' ? '#dbeafe' : '#dcfce7'};color:${item.itemType === 'product' ? '#1d4ed8' : '#166534'};padding:2px 8px;border-radius:12px;font-size:11px;">${type}</span></td>
          <td style="padding:10px 12px;text-align:center;font-weight:700;font-size:16px;">${item.quantity || 0}</td>
          <td style="padding:10px 12px;text-align:center;color:#6b7280;">${snap.unit?.abbreviation || snap.unit?.name || '-'}</td>
        </tr>`
    }).join('')

    const totalItems = po.orderItems?.length || 0
    const totalQty = parseFloat((po.orderItems?.reduce((s, i) => s + (i.quantity || 0), 0) || 0).toFixed(4))

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Purchase Order - ${po.poNumber || 'PO'}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; font-size:13px; color:#1f2937; background:#fff; padding:40px; }
        .header { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:24px; border-bottom:3px solid #ea580c; margin-bottom:28px; }
        .company { font-size:26px; font-weight:800; color:#ea580c; letter-spacing:-0.5px; }
        .company-sub { font-size:12px; color:#6b7280; margin-top:2px; }
        .po-badge { background:#ea580c; color:#fff; padding:8px 20px; border-radius:8px; text-align:center; }
        .po-badge .label { font-size:10px; letter-spacing:1px; text-transform:uppercase; opacity:.8; }
        .po-badge .number { font-size:18px; font-weight:700; margin-top:2px; }
        .status-badge { display:inline-block; padding:4px 12px; border-radius:20px; font-size:11px; font-weight:600; margin-top:6px; background:${statusConfig.bgColor.replace('bg-','').includes('yellow') ? '#fef3c7' : statusConfig.bgColor.replace('bg-','').includes('green') ? '#dcfce7' : statusConfig.bgColor.replace('bg-','').includes('blue') ? '#dbeafe' : '#f3f4f6'}; color:${statusConfig.textColor.replace('text-','').includes('yellow') ? '#92400e' : statusConfig.textColor.replace('text-','').includes('green') ? '#166534' : statusConfig.textColor.replace('text-','').includes('blue') ? '#1e40af' : '#374151'}; }
        .grid2 { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-bottom:28px; }
        .info-box { background:#f9fafb; border:1px solid #e5e7eb; border-radius:10px; padding:18px; }
        .info-box h4 { font-size:11px; text-transform:uppercase; letter-spacing:.8px; color:#6b7280; margin-bottom:12px; font-weight:600; }
        .info-row { margin-bottom:8px; }
        .info-row .lbl { font-size:11px; color:#9ca3af; margin-bottom:1px; }
        .info-row .val { font-size:13px; color:#111827; font-weight:500; }
        table { width:100%; border-collapse:collapse; margin-bottom:20px; }
        thead { background:#f3f4f6; }
        th { padding:10px 12px; text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:.5px; color:#6b7280; font-weight:600; }
        .summary { display:flex; gap:16px; margin-bottom:28px; }
        .summary-card { flex:1; background:#fff7ed; border:1px solid #fed7aa; border-radius:10px; padding:16px; text-align:center; }
        .summary-card .num { font-size:32px; font-weight:800; color:#ea580c; }
        .summary-card .lbl { font-size:11px; color:#6b7280; margin-top:4px; }
        .notes { background:#fffbeb; border:1px solid #fde68a; border-radius:10px; padding:16px; margin-bottom:28px; }
        .notes h4 { font-size:11px; text-transform:uppercase; letter-spacing:.8px; color:#92400e; margin-bottom:8px; }
        .footer { border-top:2px solid #e5e7eb; padding-top:20px; display:flex; justify-content:space-between; color:#9ca3af; font-size:11px; }
        @media print { body { padding:20px; } }
      </style></head><body>
      <div class="header">
        <div>
          <div class="company">TEZLAA</div>
          <div class="company-sub">${po.requestingBranch?.name || 'Branch'} &bull; ${po.requestingBranch?.address || ''}</div>
          <div class="status-badge">${statusConfig.label}</div>
        </div>
        <div class="po-badge">
          <div class="label">Purchase Order</div>
          <div class="number">${po.poNumber || 'N/A'}</div>
        </div>
      </div>

      <div class="grid2">
        <div class="info-box">
          <h4>Order Details</h4>
          <div class="info-row"><div class="lbl">PO Number</div><div class="val" style="font-family:monospace;">${po.poNumber || 'N/A'}</div></div>
          <div class="info-row"><div class="lbl">Order Date</div><div class="val">${fmt(po.orderDate)}</div></div>
          <div class="info-row"><div class="lbl">Expected Delivery</div><div class="val">${fmt(po.expectedDeliveryDate)}</div></div>
          <div class="info-row"><div class="lbl">Requesting Branch</div><div class="val">${po.requestingBranch?.name || 'N/A'}</div></div>
          <div class="info-row"><div class="lbl">Created By</div><div class="val">${po.createdBy?.username || 'N/A'}</div></div>
        </div>
        <div class="info-box">
          <h4>${supplierLabel} Details</h4>
          <div class="info-row"><div class="lbl">Name</div><div class="val">${supplier.name || 'N/A'}</div></div>
          <div class="info-row"><div class="lbl">Contact</div><div class="val">${supplier.contactPerson || supplier.contactName || '-'}</div></div>
          <div class="info-row"><div class="lbl">Mobile / Phone</div><div class="val">${supplier.mobile || supplier.phoneNumber || '-'}</div></div>
          <div class="info-row"><div class="lbl">Email</div><div class="val">${supplier.email || '-'}</div></div>
          <div class="info-row"><div class="lbl">Address</div><div class="val">${supplier.address || '-'}</div></div>
        </div>
      </div>

      <table>
        <thead><tr>
          <th>#</th><th>Item Name</th><th>SKU / Code</th><th>Type</th><th style="text-align:center;">Quantity</th><th style="text-align:center;">Unit</th>
        </tr></thead>
        <tbody>${itemRows || '<tr><td colspan="6" style="text-align:center;padding:20px;color:#9ca3af;">No items</td></tr>'}</tbody>
      </table>

      <div class="summary">
        <div class="summary-card"><div class="num">${totalItems}</div><div class="lbl">Total Items</div></div>
        <div class="summary-card"><div class="num">${totalQty}</div><div class="lbl">Total Quantity</div></div>
      </div>

      ${po.notes ? `<div class="notes"><h4>Notes</h4><p style="color:#78350f;font-size:13px;">${po.notes}</p></div>` : ''}

      <div class="footer">
        <span>Generated: ${new Date().toLocaleString()}</span>
        <span>Tezlaa Cafe &mdash; ${po.requestingBranch?.name || ''}</span>
        <span>For supplier use only</span>
      </div>
    </body></html>`

    const iframe = document.createElement('iframe')
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:210mm;height:297mm;'
    document.body.appendChild(iframe)
    iframe.contentDocument.open()
    iframe.contentDocument.write(html)
    iframe.contentDocument.close()
    setTimeout(() => {
      iframe.contentWindow.focus()
      iframe.contentWindow.print()
      setTimeout(() => document.body.removeChild(iframe), 1500)
    }, 400)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-gray-200 ">
          <div className="flex items-center space-x-3">
            {/* <div className="bg-blue-100 p-2 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
            </div> */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Purchase Order Details</h2>
              <p className="text-sm text-gray-600 mt-1">
                {purchaseOrder.poNumber || 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {/* Status Badge */}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.textColor} border ${statusConfig.borderColor}`}>
              <span className="mr-1">{statusConfig.icon}</span>
              {statusConfig.label}
            </span>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 p-6">
            {/* Left Panel - Order Information (2 columns) */}
            <div className="xl:col-span-2 space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-blue-600" />
                  Order Information
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">PO Number</label>
                    <p className="text-sm text-gray-900 font-mono bg-white px-3 py-2 rounded border">
                      {purchaseOrder.poNumber || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.textColor} border ${statusConfig.borderColor}`}>
                      <span className="mr-1">{statusConfig.icon}</span>
                      {statusConfig.label}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Order Date</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                      {PurchaseOrderUtils.formatDate(purchaseOrder.orderDate, 'long')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Expected Delivery</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                      {PurchaseOrderUtils.formatDate(purchaseOrder.expectedDeliveryDate, 'long')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Requesting Branch</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Building className="h-4 w-4 mr-1 text-gray-400" />
                      {purchaseOrder.requestingBranch?.name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Created By</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <User className="h-4 w-4 mr-1 text-gray-400" />
                      {purchaseOrder.createdBy?.username || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Created Date</label>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                      {PurchaseOrderUtils.formatDate(purchaseOrder.createdAt, 'time')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Supplier/Warehouse Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Building className="h-5 w-5 mr-2 text-green-600" />
                  {purchaseOrder.supplierType === 'supplier' ? 'Supplier' : 'Warehouse'} Information
                </h3>
                <div className="space-y-3">
                  {purchaseOrder.supplier && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
                        <p className="text-sm text-gray-900">{purchaseOrder.supplier.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Contact Person</label>
                        <p className="text-sm text-gray-900">{purchaseOrder.supplier.contactPerson || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Mobile</label>
                        <p className="text-sm text-gray-900">{purchaseOrder.supplier.mobile || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                        <p className="text-sm text-gray-900">{purchaseOrder.supplier.email || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Address</label>
                        <p className="text-sm text-gray-900 flex items-start">
                          <MapPin className="h-4 w-4 mr-1 text-gray-400 mt-0.5 flex-shrink-0" />
                          {purchaseOrder.supplier.address || 'N/A'}
                        </p>
                      </div>
                    </>
                  )}
                  
                  {purchaseOrder.warehouse && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
                        <p className="text-sm text-gray-900">{purchaseOrder.warehouse.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Type</label>
                        <p className="text-sm text-gray-900 capitalize">{purchaseOrder.warehouse.type || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Phone</label>
                        <p className="text-sm text-gray-900">{purchaseOrder.warehouse.phoneNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                        <p className="text-sm text-gray-900">{purchaseOrder.warehouse.email || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Address</label>
                        <p className="text-sm text-gray-900 flex items-start">
                          <MapPin className="h-4 w-4 mr-1 text-gray-400 mt-0.5 flex-shrink-0" />
                          {purchaseOrder.warehouse.address || 'N/A'}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Notes */}
              {purchaseOrder.notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-purple-600" />
                    Notes
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border">
                    {purchaseOrder.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Right Panel - Item Details (3 columns for more space) */}
            <div className="xl:col-span-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Package className="h-5 w-5 mr-2 text-orange-600" />
                  Item Details
                </h3>
                
                {/* Items Table - Scrollable when many items */}
                <div className="bg-white rounded-lg border overflow-hidden">
                  <div className="overflow-x-auto">
                    <div className="max-h-96 overflow-y-auto">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-gray-100 z-10">
                          <tr className="text-sm font-medium text-gray-700 border-b">
                            <th className="text-left p-3 min-w-[300px]">Item</th>
                            <th className="text-center p-3 w-24">Quantity</th>
                            <th className="text-center p-3 w-20">Unit</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {purchaseOrder.orderItems?.map((item, index) => {
                            const snapshot = item.productSnapshot || item.rawMaterialSnapshot || {}
                            
                            return (
                              <tr key={item._id || index} className="text-sm hover:bg-gray-50">
                                <td className="p-3">
                                  <div className="flex items-center space-x-2">
                                    <span className="font-medium text-gray-900">
                                      {snapshot.name || 'N/A'}
                                    </span>
                                    <span className="text-gray-500 text-xs">
                                      {snapshot.sku || snapshot.code || 'N/A'}
                                    </span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                      item.itemType === 'product' 
                                        ? 'bg-blue-100 text-blue-800' 
                                        : 'bg-green-100 text-green-800'
                                    }`}>
                                      {item.itemType === 'product' ? 'Product' : 'Raw Material'}
                                    </span>
                                  </div>
                                </td>
                                <td className="p-3 text-center font-medium text-lg">
                                  {PurchaseOrderUtils.formatNumber(item.quantity)}
                                </td>
                                <td className="p-3 text-center text-gray-600">
                                  {snapshot.unit?.abbreviation || snapshot.unit?.name || 'N/A'}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Items Summary */}
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded border">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-blue-600">
                        {purchaseOrder.orderItems?.length || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Total Items</p>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded border">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-600">
                        {parseFloat((purchaseOrder.orderItems?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0).toFixed(4))}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Total Quantity</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
          <div className="flex justify-end items-center gap-3 pt-6 pr-6 pb-6">
            <button
              onClick={generatePOPDF}
              className="inline-flex items-center gap-2 px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors shadow-sm"
            >
              <FileDown className="h-4 w-4" />
              Download PDF
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
      </div>
    </div>
  )
}

export default ViewPurchaseOrderModal